import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../Helpers/Pagination";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const ProfitLossReport = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  /* ================= FILTER STATE ================= */
  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  /* ================= LOAD REPORT ================= */
  const loadReport = async ({ today, dateFrom, dateTo, page }) => {

    dispatch(loadingTrue());

    const params = new URLSearchParams();
    params.append("org_id", user.org_id);
    params.append("page", page);
    params.append("limit", limit);

    if (today) params.append("today", 1);
    if (dateFrom) params.append("date_from", dateFrom.toISOString().slice(0, 10));
    if (dateTo) params.append("date_to", dateTo.toISOString().slice(0, 10));

    const res = await Handler({
      method: "get",
      url: `/reports/profit_loss.php?${params.toString()}`
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed");
      return;
    }

    const payload = res.data || res.message;

    setRows(payload?.rows || []);
    setTotals(payload?.totals || null);
    setTotal(payload?.pagination?.total_rows || 0);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.org_id) {
      setPage(1);
      loadReport({ today, dateFrom, dateTo, page: 1 });
    }
  }, [user?.org_id]);

  /* ================= AUTO FILTER ================= */
  useEffect(() => {
    if (user?.org_id) {
      setPage(1);
      loadReport({ today, dateFrom, dateTo, page: 1 });
    }
  }, [today, dateFrom, dateTo]);

  /* ================= PAGE CHANGE ================= */
  const handlePageChange = (p) => {
    setPage(p);
    loadReport({ today, dateFrom, dateTo, page: p });
  };

  /* ================= HANDLERS ================= */
  const handleToday = () => {
    setToday(!today);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleDateFrom = (d) => {
    setToday(false);
    setDateFrom(d);
  };

  const handleDateTo = (d) => {
    setToday(false);
    setDateTo(d);
  };

  /* ================= CHART DATA ================= */
  const chartData = rows.map(r => ({
    name: r.outlet_name,
    Sales: r.sales_amount,
    Cost: r.cost_amount,
    Profit: r.gross_profit
  }));

  return (
    <>

      {/* ================= FILTER BAR ================= */}
      <div className="filter-bar mb-4">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Button
              color={today ? "primary" : "outline-primary"}
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>

          <Col md={3}>
            <label className="small">Date From</label>
            <DatePicker
              selected={dateFrom}
              onChange={handleDateFrom}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
              minDate={dateFrom}
            />
          </Col>

          <Col md={3}>
            <label className="small">Date To</label>
            <DatePicker
              selected={dateTo}
              onChange={handleDateTo}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
              minDate={dateFrom}
            />
          </Col>
        </Row>
      </div>

      {/* ================= KPI CARDS ================= */}
      {totals && (
        <Row className="mb-4 g-3">
          <Col md={4}><div className="kpii-card kpii-blue"><div className="kpii-label">Sales</div><div className="kpii-value">₹ {Number(totals.sales_amount).toFixed(2)}</div></div></Col>
          <Col md={4}><div className="kpii-card kpii-purple"><div className="kpii-label">Purchase Cost</div><div className="kpii-value">₹ {Number(totals.cost_amount).toFixed(2)}</div></div></Col>
          <Col md={4}><div className="kpii-card kpii-green"><div className="kpii-label">Gross Profit</div><div className="kpii-value">₹ {Number(totals.gross_profit).toFixed(2)}</div></div></Col>
          {/* <Col md={3}><div className="kpii-card kpii-red"><div className="kpii-label">Outstanding</div><div className="kpii-value">₹ {Number(totals.outstanding).toFixed(2)}</div></div></Col> */}
        </Row>
      )}

      {/* ================= BAR CHART ================= */}
      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={12}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Sales" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Cost" fill="#ef4444" radius={[6, 6, 0, 0]} />
                <Bar dataKey="Profit" fill="#22c55e" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <div className="billing-table-card">
        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Outlet</th>
                <th className="right">Sales</th>
                <th className="right">Cost</th>
                <th className="right">Profit</th>
                <th className="right">Collections</th>
                <th className="right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.outlet_name}</td>
                  <td className="right">₹ {Number(r.sales_amount).toFixed(2)}</td>
                  <td className="right">₹ {Number(r.cost_amount).toFixed(2)}</td>
                  <td className={`right ${r.gross_profit > 0 ? "success" : "danger"}`}>
                    ₹ {Number(r.gross_profit).toFixed(2)}
                  </td>
                  <td className="right">₹ {Number(r.collections).toFixed(2)}</td>
                  <td className={`right ${r.outstanding > 0 ? "danger" : "success"}`}>
                    ₹ {Number(r.outstanding).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ================= PAGINATION ================= */}
        <Pagination
          page={page}
          total={total}
          limit={limit}
          onChange={handlePageChange}
        />
      </div>

    </>
  );
};

export default ProfitLossReport;
